import { kopecks, type Kopecks } from '@glory/kernel';

import type { DocumentStatus, UploadKind } from './kinds.ts';

/** День `ГГГГ-ММ-ДД` по поясу студии. */
export type IsoDate = string;

/** Объекты журнала и живых обновлений: документ, вид документа с его текстами, настройки документов. */
export const DOCUMENT_ENTITY = 'document';
export const DOCUMENT_TEMPLATE_ENTITY = 'document_template';
export const DOCUMENT_SETTINGS_ENTITY = 'document_settings';
/** Пакет на подпись: «История» подписания — заморозка, подписи, отзыв, скан. */
export const PACKAGE_ENTITY = 'document_package';
/** Настройки документов — одна запись на студию (UUID версии 8): общая «История» параметров. */
export const DOCUMENT_SETTINGS_ENTITY_ID = '00000000-0000-8000-8000-000000000005';

export const DOCUMENT_TITLE_MAX = 300;
export const DOCUMENT_NUMBER_MAX = 60;
export const TEMPLATE_TITLE_MAX = 120;

/**
 * Загруженный вручную документ: файл, вид, номер (если есть), дата, привязка к клиенту, сделке или сотруднику и
 * отметка «подписан» (раздел 5, «Хранилище»). Файл после сохранения не меняется — замена создаёт новый документ.
 */
export interface UploadFields {
  readonly kind: UploadKind;
  readonly title: string;
  readonly number: string | null;
  readonly issuedOn: IsoDate;
  readonly signed: boolean;
  readonly clientId: string | null;
  readonly dealId: string | null;
  readonly employeeId: string | null;
  /** Сумма в копейках; у счёта обязательна (по ней он попадает в «Счета»), у акта — если известна. */
  readonly amount?: number | null;
}

/** Виды загруженного документа, у которых есть сумма: счёт и акт. Остальным сумму не храним. */
const AMOUNT_KINDS: readonly string[] = ['invoice', 'act'];

export const storedAmountOf = (kind: UploadKind, amount: number | null | undefined): Kopecks | null =>
  amount !== null && amount !== undefined && AMOUNT_KINDS.includes(kind) ? kopecks(amount) : null;

export type UploadIssue =
  'title_required' | 'title_too_long' | 'number_too_long' | 'date_invalid' | 'amount_required';

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** `YYYY-MM-DD`, и такой день есть в календаре: `2026-02-30` — нет (`Date` перенёс бы его на 2 марта). */
function isCalendarDate(value: string): boolean {
  if (!DATE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

/** Текст поля без лишних пробелов; пусто — null. */
export function cleanText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const clean = value.replace(/\s+/g, ' ').trim();
  return clean === '' ? null : clean;
}

export function uploadIssues(
  fields: Pick<UploadFields, 'title' | 'number' | 'issuedOn' | 'amount'> & {
    readonly kind?: UploadKind | null;
  },
): UploadIssue[] {
  const issues: UploadIssue[] = [];
  const title = cleanText(fields.title);
  if (title === null) issues.push('title_required');
  else if (title.length > DOCUMENT_TITLE_MAX) issues.push('title_too_long');
  if ((cleanText(fields.number)?.length ?? 0) > DOCUMENT_NUMBER_MAX) issues.push('number_too_long');
  if (!isCalendarDate(fields.issuedOn)) issues.push('date_invalid');
  if (fields.kind === 'invoice' && (fields.amount ?? null) === null) issues.push('amount_required');
  return issues;
}

/** Что можно сделать с документом в его состоянии: удалить — только черновик; в архив — всё, кроме черновика. */
export const canDeleteDocument = (status: DocumentStatus): boolean => status === 'draft';
export const canArchiveDocument = (status: DocumentStatus): boolean => status !== 'draft';
